import { useEffect, useRef } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Shield } from 'lucide-react';
import {
  Switch, 
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useActiveSlaTemplates } from '@/api/sla-templates';
import { FieldGrid } from '../section-shell';
import { ConfigToggleHeader } from './config-toggle-header';
import { SlaRulesEditor } from './sla-rules-editor';
import type { ContractFormValues } from '../contract-form';
import { NumericInput } from '@/components/numeric-input';

/** A bound number input for a top-level `service_sla.{field}` path. */
function SlaNumber({
  field, label, step, description,
}: {
  field: 'uptime_guarantee' | 'downtime_allowance_minutes';
  label: string; step?: string; description?: string;
}) {
  const { control } = useFormContext<ContractFormValues>();
  return (
    <Controller
      control={control}
      name={`service_sla.${field}` as const}
      render={({ field: f }) => (
        <Field label={label} description={description}>
          {(p) => (
            <NumericInput
              {...p}
              min={0}
              step={step}
              value={f.value == null ? '' : String(f.value)}
              onChange={(e) => f.onChange(Number(e.target.value))}
              onBlur={f.onBlur}
              name={f.name}
              ref={f.ref}
            />
          )}
        </Field>
      )}
    />
  );
}

/**
 * SLA configuration — a switch (`sla_enable`) reveals the SLA builder. Source
 * toggles template|custom: `template` offers the active SLA tiers (metrics
 * auto-fill on pick, still editable), `custom` lets the user set them directly.
 * Uptime %, downtime allowance, business-hours-only, and the editable rule list
 * round-trip into the payload's `service_sla`. Mirrors legacy SLAConfigSection.
 */
export function SlaConfigSection() {
  const { control, setValue } = useFormContext<ContractFormValues>();
  const enabled = useWatch({ control, name: 'sla_enable' });
  const source = useWatch({ control, name: 'service_sla.sla_source' });
  const templateId = useWatch({ control, name: 'service_sla.sla_template_id' });
  const isTemplate = source === 'template';

  const { data: templates = [] } = useActiveSlaTemplates(enabled && isTemplate);

  // Auto-fill metrics when a template is picked (legacy select→setValue effect).
  const appliedRef = useRef<number>(0);
  useEffect(() => {
    if (!isTemplate || !templateId) return;
    if (appliedRef.current === templateId) return;
    const t = templates.find((tpl) => tpl.sla_template_id === templateId);
    if (!t) return;
    appliedRef.current = templateId;
    setValue('service_sla.uptime_guarantee', t.uptime_guarantee);
    setValue('service_sla.downtime_allowance_minutes', t.downtime_allowance_minutes);
    setValue('service_sla.business_hours_only', t.business_hours_only);
    if (t.default_response_time || t.default_resolution_time) {
      setValue('service_sla.sla_rules', [
        {
          ticket_type_group: 'default',
          response_time_minutes: t.default_response_time,
          resolution_time_minutes: t.default_resolution_time,
        },
      ]);
    }
  }, [isTemplate, templateId, templates, setValue]);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <ConfigToggleHeader
        name="sla_enable"
        icon={<Shield size={16} />}
        title="Enable SLA"
        description="Guarantee uptime and support response targets."
      />

      {enabled ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <FieldGrid>
            <Field label="Source">
              {(p) => (
                <Select
                  value={source}
                  onValueChange={(v) => setValue('service_sla.sla_source', v as 'template' | 'custom')}
                >
                  <SelectTrigger {...p}>
                    <SelectValue placeholder="Select a source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="template">From template</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </Field>

            {isTemplate ? (
              <Field label="SLA template" description="Picking a tier fills the metrics below.">
                {(p) => (
                  <Select
                    value={templateId > 0 ? String(templateId) : ''}
                    onValueChange={(v) => setValue('service_sla.sla_template_id', Number(v))}
                  >
                    <SelectTrigger {...p}>
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.sla_template_id} value={String(t.sla_template_id)}>
                          {t.template_name}
                          {t.uptime_guarantee != null ? ` (${t.uptime_guarantee}%)` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            ) : null}
          </FieldGrid>

          <FieldGrid>
            <SlaNumber
              field="uptime_guarantee"
              label="Uptime guarantee (%)"
              step="0.01"
              description="e.g. 99.9"
            />
            <SlaNumber
              field="downtime_allowance_minutes"
              label="Downtime allowance (min/month)"
            />
          </FieldGrid>

          <Controller
            control={control}
            name="service_sla.business_hours_only"
            render={({ field }) => (
              <label
                style={{ display: 'flex', gap: 'var(--vn-space-3)', alignItems: 'center', cursor: 'pointer' }}
              >
                <Switch
                  checked={!!field.value}
                  onCheckedChange={field.onChange}
                  aria-label="Business hours only"
                />
                <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>
                  Apply during business hours only
                  <span style={{ color: 'var(--vn-fg-muted)' }}>
                    {' '}(otherwise 24/7)
                  </span>
                </span>
              </label>
            )}
          />

          <SlaRulesEditor />
        </div>
      ) : null}
    </div>
  );
}
